export default {
  // Navigation
  nav: {
    home: 'Home',
    features: 'Features',
    who: 'Who It Is For',
    integrations: 'Integrations',
    pricing: 'Pricing',
    contact: 'Contact',
    howItWorks: 'How It Works',
    privacy: 'Privacy Policy',
    terms: 'Terms of Use',
    demo: 'Request a Demo'
  },

  // Global
  common: {
    requestDemo: 'Request a Demo',
    seeHowItWorks: 'See How It Works',
    learnMore: 'Learn More',
    getStarted: 'Get Started',
    manage: 'Manage'
  },

  // Home page
  home: {
    hero: {
      headline: 'Easy To Manage (E2M) — Control All Your Online Channels from One Place',
      subheadline: 'Centralize listings, inventory, performance data, and product images across Amazon, Shopify, TikTok, Walmart, and Target.',
      primaryCTA: 'Request a Demo',
      secondaryCTA: 'See How It Works'
    },
    problem: {
      title: 'One Control Center for All Your Marketplaces',
      description: 'Managing multiple marketplaces is messy. E2M unifies listings, images, and performance into one control center, so you can focus on growth instead of chaos.'
    },
    keyFeatures: {
      title: 'Everything You Need in One Platform',
      productListings: {
        title: 'Product & Listings',
        description: 'Manage product information and listings across all channels from one central hub.'
      },
      imageManagement: {
        title: 'Image Management',
        description: 'AI-powered infographics and lifestyle image generation for all your products.'
      },
      performanceReports: {
        title: 'Performance & Reports',
        description: 'Track KPIs grouped by data areas: sales, listings, inventory, and ads.'
      }
    },
    footerCTA: {
      title: 'Ready to Simplify Your Online Retail Management?',
      description: 'Join brands that trust E2M to streamline their multi-channel operations.',
      cta: 'Get Started Today'
    }
  },

  // Features page
  features: {
    title: 'Everything You Need to Manage Online Retail — From Listings to Analytics',
    detailed: {
      productListingManagement: {
        title: 'Product & Listing Management',
        description: 'Bulk updates via Excel/API. Ensure cross-channel consistency with centralized product data management.'
      },
      imageManagementAI: {
        title: 'Image Management & AI',
        description: 'Associate visuals to variations, detect missing images, and generate AI-powered infographics and lifestyle images.'
      },
      reportingInsights: {
        title: 'Reporting & Insights',
        description: 'Track performance by channel, category, and product. KPIs grouped by sales, listings, inventory, and ads data areas.'
      },
      adPerformanceMonitoring: {
        title: 'Ad Performance Monitoring',
        description: 'Monitor Amazon Ads metrics including impressions, clicks, conversions, and costs. Track search-term ranking trends.'
      },
      inventoryOrders: {
        title: 'Inventory & Orders',
        description: 'Maintain stock visibility across channels, deploy orders from your warehouse or deliver from Amazon MCF, automatically update quantities in real-time, and avoid overselling.'
      },
      taskManagementWorkflows: {
        title: 'Task Management & Workflows',
        description: 'Assign actions, track progress, and link tasks to specific listings and data issues for better accountability.'
      }
    }
  },

  // Who page
  who: {
    title: 'Built for Brand Owners and Teams Managing Online Growth',
    roles: {
      management: {
        title: 'Management',
        description: 'Monitor multi-market performance and KPIs from a unified dashboard to make informed strategic decisions.'
      },
      listingManagers: {
        title: 'Online Listing Managers / Marketing Ops',
        description: 'Manage copy, product information, images, and prices across all marketplaces from one central location.'
      },
      inventoryFulfillment: {
        title: 'Inventory & Fulfillment',
        description: 'Track stock levels, update quantities, and sync data across all channels to prevent overselling.'
      },
      operationsSupport: {
        title: 'Operations / Support',
        description: 'Follow task progress and handle product-related inquiries with complete visibility into all channels.'
      }
    }
  },

  // Integrations page
  integrations: {
    title: 'Connected with Your Main Marketplaces',
    subtitle: 'Seamlessly integrate with the platforms you already use',
    amazon: {
      description: 'Full integration with Amazon Seller Central, including inventory, listings, and advertising data.'
    },
    shopify: {
      description: 'Connect your Shopify store for unified product and inventory management.'
    },
    walmart: {
      description: 'Manage your Walmart Marketplace listings and performance data.'
    },
    tiktokShop: {
      description: 'Integrate TikTok Shop for social commerce management and analytics.'
    },
    target: {
      description: 'Connect with Target Plus for comprehensive marketplace coverage.'
    },
    status: {
      connected: 'Connected'
    }
  },

  // How It Works page
  howItWorks: {
    title: 'How E2M Works',
    subtitle: 'Connect your marketplaces and start managing everything from one unified platform.',
    steps: {
      connect: {
        title: 'Connect Your Channels',
        description: 'Securely integrate all your marketplace accounts with E2M. Our platform supports Amazon, Shopify, Walmart, TikTok Shop, and Target.'
      },
      centralize: {
        title: 'Centralize Your Data',
        description: 'All your product listings, inventory levels, sales data, and performance metrics are consolidated into one unified dashboard for complete visibility.'
      },
      optimize: {
        title: 'Optimize & Manage',
        description: 'Use our AI-powered tools to enhance product listings, generate compelling images, and bulk update information across all channels simultaneously.'
      },
      grow: {
        title: 'Scale & Grow',
        description: 'Monitor performance, track KPIs, and scale your operations with confidence using our comprehensive analytics and automated workflows.'
      }
    },
    cta: {
      title: 'Ready to Transform Your Retail Operations?',
      description: 'Join successful brands who have streamlined their multi-channel management with E2M.'
    }
  },

  // Privacy Policy page
  privacy: {
    title: 'Privacy Policy',
    lastUpdated: 'Last updated: April 1, 2024'
  },

  // Terms of Use page
  terms: {
    title: 'Terms of Service',
    lastUpdated: 'Last updated: October 29, 2025'
  },

  // Demo page
  demo: {
    title: 'Schedule Your E2M Demo',
    subtitle: 'See how E2M can transform your multi-channel retail operations'
  },

  // Contact page
  contact: {
    title: 'Get in Touch',
    subtitle: 'Ready to streamline your online retail management? Let us talk.'
  },

  // Pricing page
  pricing: {
    title: 'Simple, Transparent Pricing',
    subtitle: 'No revenue share. No hidden fees.',
    tier: {
      name: 'Professional Plan',
      priceNote: 'Annual subscription based on order volume and add-ons'
    },
    features: {
      allIntegrations: 'All Integrations',
      salesForecasting: 'Sales Forecasting',
      unlimitedUsers: 'Unlimited Users',
      inventoryManagement: 'Inventory Management',
      warehouseManagement: 'Warehouse Management',
      productManagement: 'Product Management',
      orderManagement: 'Order Management',
      listingManagement: 'Listing Management',
      dedicatedSupport: 'Dedicated Support',
      aiImageGeneration: 'AI Image Generation',
      performanceAnalytics: 'Performance Analytics & Reports',
      adMonitoring: 'Ad Performance Monitoring',
      taskWorkflows: 'Task Management & Workflows',
      bulkUpdates: 'Bulk Updates via Excel/API',
      syncedListings: 'Synced Listing Info (Prices, Inventory, Descriptions)',
      customReports: 'Custom Reports & Insights'
    },
    benefits: {
      title: 'One Platform, Complete Control',
      subtitle: 'Focus on growing your business while E2M handles the complexity of managing multiple marketplaces from a single, unified platform.',
      noUserFee: 'Unlimited team members',
      noRevenueShare: 'No revenue share',
      consolidatedData: 'All your data in one place'
    },
    cta: {
      requestDemo: 'Request Demo',
      contactSales: 'Contact Sales'
    }
  },

  // KPI Areas
  kpis: {
    areas: {
      sales: 'Sales',
      listings: 'Listings',
      inventory: 'Inventory',
      ads: 'Ads'
    }
  }
}